import { CvtRepairDisposition } from '@packages/models/api';
import { Button, Typography } from '@packages/ui/shared';
import { ChangeEvent, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

type CvtRepairDispositionModalProps = {
    isApproving: boolean;
    onApprove: (repairDisposition: CvtRepairDisposition) => void;
    onExited: () => void;
    onHide: () => void;
    show: boolean;
};

/**
 * The modal where a DSQM picks the authorized CVT repair before approving a NQR
 *
 * Renders CVT Repair and CVT Replacement as radio buttons, and enables the Approve button once one is selected.
 */
export function CvtRepairDispositionModal({
    isApproving,
    onApprove,
    onExited,
    onHide,
    show,
}: CvtRepairDispositionModalProps) {
    const { t } = useTranslation();
    const [repairDispositionValue, setRepairDispositionValue] = useState<CvtRepairDisposition | null>(null);

    function handleApprove() {
        if (repairDispositionValue) {
            onApprove(repairDispositionValue);
        }
    }

    function handleExited() {
        setRepairDispositionValue(null);
        onExited();
    }

    function handleRadioChange(event: ChangeEvent<HTMLInputElement>) {
        setRepairDispositionValue(event.target.value as CvtRepairDisposition);
    }

    return (
        <Modal
            backdrop={isApproving ? 'static' : true}
            centered
            keyboard={!isApproving}
            onExited={handleExited}
            onHide={onHide}
            show={show}
        >
            <Modal.Header closeButton={!isApproving}>
                <Modal.Title>{t('modals:cvtRepairDisposition.title', 'Confirm CVT Approval')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group>
                    <div className="mb-4">
                        <Typography variant="label">
                            {t(
                                'modals:cvtRepairDisposition.instructions',
                                'The technician disagreed with the CVT repair recommendation. Please select a recommendation before approving.'
                            )}
                        </Typography>
                    </div>
                    <Form.Check
                        checked={repairDispositionValue === CvtRepairDisposition.CvtRepair}
                        className="mt-2"
                        disabled={isApproving}
                        id="cvt-repair"
                        label={<Typography>{t('modals:cvtRepairDisposition.cvtRepair', 'CVT Repair')}</Typography>}
                        name="cvt-repair-disposition"
                        onChange={handleRadioChange}
                        type="radio"
                        value={CvtRepairDisposition.CvtRepair}
                    />
                    <Form.Check
                        checked={repairDispositionValue === CvtRepairDisposition.CvtReplacement}
                        className="mt-2"
                        disabled={isApproving}
                        id="cvt-replacement"
                        label={
                            <Typography>
                                {t('modals:cvtRepairDisposition.cvtReplacement', 'CVT Replacement')}
                            </Typography>
                        }
                        name="cvt-repair-disposition"
                        onChange={handleRadioChange}
                        type="radio"
                        value={CvtRepairDisposition.CvtReplacement}
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button disabled={isApproving} onPress={onHide} variant="ghost-blue">
                    {t('buttons:cancel', 'Cancel')}
                </Button>
                <Button
                    disabled={!repairDispositionValue || isApproving}
                    isLoading={isApproving}
                    onPress={handleApprove}
                >
                    {t('modals:cvtRepairDisposition.buttons.approve', 'Approve')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
